// ===========================================================================
// Depth Proxy Module
// Generates synthetic depth maps for the simulation
// ===========================================================================

import {
  GRID_COLS,
  GRID_ROWS,
  WORLD_WIDTH,
  WORLD_HEIGHT,
  SimulationParams,
} from '../types';

/**
 * Create an empty depth grid
 */
export function createDepthGrid(): number[][] {
  return Array.from({ length: GRID_ROWS }, () =>
    Array.from({ length: GRID_COLS }, () => 0)
  );
}

/**
 * Compute depth grid based on selected mode
 */
export function computeDepthGrid(
  params: SimulationParams,
  vanishingPoint?: { x: number; y: number }
): number[][] {
  const grid = createDepthGrid();
  const vp = vanishingPoint || { x: WORLD_WIDTH / 2, y: 0 };

  for (let row = 0; row < GRID_ROWS; row++) {
    for (let col = 0; col < GRID_COLS; col++) {
      // Convert grid coords to world coords
      const worldX = (col / GRID_COLS) * WORLD_WIDTH;
      const worldY = (row / GRID_ROWS) * WORLD_HEIGHT;

      let depth: number;

      if (params.depthMode === 'perspective-y') {
        // Depth increases with Y (farther = smaller Y, closer = larger Y)
        // Normalized to [0, 1] where 0 = far (top), 1 = close (bottom)
        depth = worldY / WORLD_HEIGHT;
      } else {
        // Radial depth from vanishing point
        const dx = worldX - vp.x;
        const dy = worldY - vp.y;
        const maxDist = Math.sqrt(WORLD_WIDTH ** 2 + WORLD_HEIGHT ** 2);
        depth = Math.sqrt(dx * dx + dy * dy) / maxDist;
      }

      // Add noise
      if (params.depthNoise > 0) {
        depth += gaussianNoise(params.depthNoise);
        depth = clamp(depth, 0, 1);
      }

      grid[row][col] = depth;
    }
  }

  return grid;
}

/**
 * Sample depth at a world position from the grid
 */
export function sampleDepthAt(
  depthGrid: number[][],
  worldX: number,
  worldY: number
): number {
  const col = Math.floor((worldX / WORLD_WIDTH) * GRID_COLS);
  const row = Math.floor((worldY / WORLD_HEIGHT) * GRID_ROWS);

  const safeRow = clamp(row, 0, GRID_ROWS - 1);
  const safeCol = clamp(col, 0, GRID_COLS - 1);

  return depthGrid[safeRow][safeCol];
}

/**
 * Sample depth with bilinear interpolation
 */
export function sampleDepthBilinear(
  depthGrid: number[][],
  worldX: number,
  worldY: number
): number {
  const gx = (worldX / WORLD_WIDTH) * GRID_COLS - 0.5;
  const gy = (worldY / WORLD_HEIGHT) * GRID_ROWS - 0.5;

  const x0 = Math.floor(gx);
  const y0 = Math.floor(gy);
  const x1 = x0 + 1;
  const y1 = y0 + 1;

  const fx = gx - x0;
  const fy = gy - y0;

  const sx0 = clamp(x0, 0, GRID_COLS - 1);
  const sx1 = clamp(x1, 0, GRID_COLS - 1);
  const sy0 = clamp(y0, 0, GRID_ROWS - 1);
  const sy1 = clamp(y1, 0, GRID_ROWS - 1);

  const d00 = depthGrid[sy0][sx0];
  const d10 = depthGrid[sy0][sx1];
  const d01 = depthGrid[sy1][sx0];
  const d11 = depthGrid[sy1][sx1];

  const d0 = d00 * (1 - fx) + d10 * fx;
  const d1 = d01 * (1 - fx) + d11 * fx;

  return d0 * (1 - fy) + d1 * fy;
}

/**
 * Get depth values at multiple points
 */
export function getDepthsAtPoints(
  depthGrid: number[][],
  points: Array<{ x: number; y: number }>
): number[] {
  return points.map((p) => sampleDepthBilinear(depthGrid, p.x, p.y));
}

// ----------------------------- Utility Functions ---------------------------

function gaussianNoise(stddev: number): number {
  const u1 = Math.random();
  const u2 = Math.random();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return z * stddev;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
